import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../../api/client';

export default function LoginScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      if (data.user.mustChangePassword) {
        sessionStorage.setItem('tempPw', password);
        navigate('/change-password');
      } else if (data.user.role === 'CLIENT' && data.user.clientId) {
        localStorage.setItem('activeClient', JSON.stringify({ id: data.user.clientId, name: data.user.name }));
        navigate('/dashboard');
      } else {
        navigate('/clients');
      }
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError('Unable to reach the server. Please try again.');
      } else if (err.message?.includes('verify your email')) {
        setError('Please verify your email before logging in. Check your inbox and spam folder.');
      } else {
        setError(err.message || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center mb-3 gap-3">
            <a href="https://brain-centric.com/" target="_blank" rel="noopener noreferrer">
              <img src="/bcd-logo.png" alt="Brain-Centric Design" className="h-16 w-auto" />
            </a>
            <h1 className="text-[40px] leading-none font-heading font-semibold text-white">
              The Clarity Map
            </h1>
          </div>
          <p className="text-white mt-3 text-sm font-body font-bold">
            Turn everything you know into the one thing they remember.
          </p>
          <p className="text-gray-400 mt-2 text-sm font-body leading-relaxed">
            You walk away with your Big Idea and what success looks like to you. The things your audience must understand to believe it.
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <h2 className="text-xl font-heading font-semibold text-txt-primary mb-6 text-center">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-txt-secondary mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm
                  focus:border-gold focus:ring-gold text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-txt-secondary mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm
                  focus:border-gold focus:ring-gold text-sm"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full text-center disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="text-center mt-2">
              <Link to="/forgot-password" className="text-xs text-txt-secondary hover:text-gold-dark hover:underline">
                Forgot password?
              </Link>
            </div>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-txt-secondary">New to The Clarity Map?</span>
              </div>
            </div>

            <Link
              to="/signup"
              className="block w-full text-center rounded-lg border-2 border-gold text-gold-dark font-semibold py-2.5 text-sm hover:bg-gold/10 transition-colors"
            >
              Start your free trial
            </Link>
            <p className="text-center text-xs text-txt-secondary mt-2">
              Free 7 days. No credit card required.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
